/**
 * Role definitions for the OMS application
 *
 * Using an additive model: each role grants specific permissions.
 * Users can have multiple roles, and permissions are combined.
 *
 * @example
 * // User with both ADMIN and SENDSERV_ADMIN roles
 * user.roles = ['ADMIN', 'SENDSERV_ADMIN']
 */

export const ROLES = {
  STANDARD: 'STANDARD',
  ADMIN: 'ADMIN',
  SENDSERV_ADMIN: 'SENDSERV_ADMIN',
} as const;

export type Role = (typeof ROLES)[keyof typeof ROLES];

export const ALL_ROLES: Role[] = Object.values(ROLES);

export const DEFAULT_ROLE: Role = ROLES.STANDARD;

/**
 * Check if a user has the required role(s)
 * When multiple roles are passed, returns true if user has ANY of them (OR logic)
 * @param userRoles - Array of roles the user has
 * @param requiredRoles - Role(s) to check for
 * @returns boolean
 */
export function hasRole(
  userRoles: string[] | null | undefined,
  requiredRoles: string | string[]
): boolean {
  if (!userRoles || !Array.isArray(userRoles)) return false;

  const required = Array.isArray(requiredRoles) ? requiredRoles : [requiredRoles];
  return required.some((role) => userRoles.includes(role));
}

/**
 * Check if a user has ALL of the required roles (AND logic)
 * @param userRoles - Array of roles the user has
 * @param requiredRoles - Roles to check for
 * @returns boolean
 */
export function hasAllRoles(
  userRoles: string[] | null | undefined,
  requiredRoles: string[]
): boolean {
  if (!userRoles || !Array.isArray(userRoles)) return false;

  return requiredRoles.every((role) => userRoles.includes(role));
}
